import {
    serializable,
    DynamicFormControlLayout,
    DynamicInputModel,
    DynamicInputModelConfig,
    MATCH_VISIBLE,
} from "@ng-dynamic-forms/core";
import { Model_WorkflowComponent, CODE_WORKFLOW_COMPONENT, TYPE_DATASOURCE, TYPE_WORKFLOW_COMPONENT_OBSERVER, TYPE_DEFAULT_VALUE } from 'src/app/app.models';
import { COMPONENT_MODE, COMPONENT_UI } from "../../workflow-components.models";
import { WorkflowComponentBuilderOptions } from "../../workflow-components.service";
  
export interface MultipackCommercialPlanningModelConfig extends DynamicInputModelConfig {
    componentMode: COMPONENT_MODE;
    showOnlyAuthenticatedUser?: boolean;
}
  
export class MultipackCommercialPlanningModel extends DynamicInputModel {
    @serializable() readonly type: string = 'multipack_commercial_planning'; 
    componentMode: COMPONENT_MODE;
    componentUI: COMPONENT_UI;
    showOnlyAuthenticatedUser?: boolean;

    constructor(config: MultipackCommercialPlanningModelConfig, layout?: DynamicFormControlLayout) {
        super(config, layout);
        this.componentMode = config.componentMode;
        this.componentUI = COMPONENT_UI.KENDO;
        this.showOnlyAuthenticatedUser = config.additional?.SHOW_ONLY_AUTHENTICATED_USER ?? false;
    }

    static initModel(_component: Model_WorkflowComponent): MultipackCommercialPlanningModel {
        const formModel = new MultipackCommercialPlanningModel({
            id: _component.ID,
            name: _component.ID,
            label: _component.LABEL,
            value: _component.DEFAULT_VALUE,
            required: _component.IS_REQUIRED,
            readOnly: _component.IS_READONLY,
            hidden: _component.IS_VISIBLE !== undefined ? _component.IS_VISIBLE === false : false,
            validators: _component.CUSTOM_VALIDATORS,
            componentMode: _component.COMPONENT_MODE,
            additional: _component,
            relations: _component.RELATIONS,
        });
        return formModel;
    }

    static getProperties(_options?: WorkflowComponentBuilderOptions): Model_WorkflowComponent {
        return {
            ID: 'multipack_commercial_planning',
            CODE: CODE_WORKFLOW_COMPONENT.MULTIPACK_COMMERCIAL_PLANNING,
            ICON: 'create-outline',
            LABEL: '[MULTIPACK] - Planning Commercial',
            PROPERTIES: [
                { ID: 'GUID', CODE: CODE_WORKFLOW_COMPONENT.TEXTBOX, LABEL: 'GUID', IS_VISIBLE: false },
                { ID: 'INDEX', CODE: CODE_WORKFLOW_COMPONENT.NUMERIC_TEXTBOX, LABEL: 'Index', DEFAULT_VALUE: 1 },
                { ID: 'ID', CODE: CODE_WORKFLOW_COMPONENT.TEXTBOX, LABEL: 'Identifiant', IS_REQUIRED: true, CUSTOM_VALIDATORS: [{ VALIDATOR_NAME: 'no_whitespace' }] },
                { ID: 'LABEL', CODE: CODE_WORKFLOW_COMPONENT.TEXTBOX, LABEL: 'Libellé', IS_REQUIRED: true },
				{ ID: 'IS_READONLY', CODE: CODE_WORKFLOW_COMPONENT.TOGGLE, LABEL: 'Lecture Seule ?' },
				{ ID: 'SHOW_ONLY_AUTHENTICATED_USER', CODE: CODE_WORKFLOW_COMPONENT.TOGGLE, LABEL: 'Afficher uniquement le planning de l\'agent connecté ?' },
                { ID: 'NUM_WORKFLOW_COLUMNS_MAPPING', CODE: CODE_WORKFLOW_COMPONENT.TEXTBOX, LABEL: 'Num Workflow Conception', IS_REQUIRED: true },
                { ID: 'NUM_DATASOURCE_AGENTS', CODE: CODE_WORKFLOW_COMPONENT.DATASOURCE_PICKER, LABEL: 'Agents', IS_REQUIRED: true, HAS_DATASOURCE_FILTERS: false },
                { ID: 'ID_DATASOURCE_AGENTS_COLUMN', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Identifiant Agents', IS_REQUIRED: true, 
                    UIC_PARAMS: {
                        OBSERVERS: [
                            { field: 'NUM_DATASOURCE_AGENTS', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
                        ],
                        DATASOURCE: {
                            DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
                            DATASOURCE_DATAS: [],
                            DATASOURCE_VALUE_FIELD: 'value',
                            DATASOURCE_DISPLAY_FIELD: 'display'
                        }
                    }
                },
                { ID: 'FULLNAME_DATASOURCE_AGENTS_COLUMN', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Nom & Prénoms Agents', IS_REQUIRED: true, 
                    UIC_PARAMS: {
                        OBSERVERS: [
                            { field: 'NUM_DATASOURCE_AGENTS', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
                        ],
                        DATASOURCE: {
                            DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
                            DATASOURCE_DATAS: [],
                            DATASOURCE_VALUE_FIELD: 'value',
                            DATASOURCE_DISPLAY_FIELD: 'display'
                        }
                    }
                },
                { ID: 'NUM_DATASOURCE_TIERS', CODE: CODE_WORKFLOW_COMPONENT.DATASOURCE_PICKER, LABEL: 'Tiers', IS_REQUIRED: true, HAS_DATASOURCE_FILTERS: false },
                { ID: 'ID_DATASOURCE_TIERS_COLUMN', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Identifiant Tiers', IS_REQUIRED: true, 
                    UIC_PARAMS: {
                        OBSERVERS: [
                            { field: 'NUM_DATASOURCE_TIERS', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
                        ],
                        DATASOURCE: {
                            DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
                            DATASOURCE_DATAS: [],
                            DATASOURCE_VALUE_FIELD: 'value',
                            DATASOURCE_DISPLAY_FIELD: 'display'
                        }
                    }
                },
                { ID: 'FULLNAME_DATASOURCE_TIERS_COLUMN', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Nom & Prénoms Tiers', IS_REQUIRED: true, 
                    UIC_PARAMS: {
                        OBSERVERS: [
                            { field: 'NUM_DATASOURCE_TIERS', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
                        ],
                        DATASOURCE: {
                            DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
                            DATASOURCE_DATAS: [],
                            DATASOURCE_VALUE_FIELD: 'value',
                            DATASOURCE_DISPLAY_FIELD: 'display'
                        }
                    }
                },
                { ID: 'TYPE_DATASOURCE_TIERS_COLUMN', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Type Tiers', IS_REQUIRED: true, 
                    UIC_PARAMS: {
                        OBSERVERS: [
                            { field: 'NUM_DATASOURCE_TIERS', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
                        ],
                        DATASOURCE: {
                            DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
                            DATASOURCE_DATAS: [],
                            DATASOURCE_VALUE_FIELD: 'value',
                            DATASOURCE_DISPLAY_FIELD: 'display'
                        }
                    }
                },
                { ID: 'SOLDE_DATASOURCE_TIERS_COLUMN', CODE: CODE_WORKFLOW_COMPONENT.DROPDOWN, LABEL: 'Solde Tiers', IS_REQUIRED: true, 
                    UIC_PARAMS: {
                        OBSERVERS: [
                            { field: 'NUM_DATASOURCE_TIERS', type: TYPE_WORKFLOW_COMPONENT_OBSERVER.OBSERVE_DATASOURCE_STRUCTURE }
                        ],
                        DATASOURCE: {
                            DATASOURCE_TYPE: TYPE_DATASOURCE.STATIC,
                            DATASOURCE_DATAS: [],
                            DATASOURCE_VALUE_FIELD: 'value',
                            DATASOURCE_DISPLAY_FIELD: 'display'
                        }
                    }
                },
            ]
        };
    }
}
  